import React, { useState, useRef } from 'react';
import { Building2, Upload, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { vendorsAPI } from '../services/api';

interface UploadForm {
  file: File | null;
}

interface Errors {
  file?: string;
}

interface ImportError {
  row: number;
  vendor: string;
  error: string;
  vendorData?: {
    name: string;
    department: string;
  };
}

interface ImportResult {
  total: number;
  successful: number;
  failed: number;
  errors: ImportError[];
}

interface VendorData {
  name: string;
  department: string;
}

const ImportVendor: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadForm, setUploadForm] = useState<UploadForm>({
    file: null,
  });
  const [errors, setErrors] = useState<Errors>({});
  const [importStatus, setImportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [importMessage, setImportMessage] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [failedVendors, setFailedVendors] = useState<VendorData[]>([]);

  const handleExportExcel = () => {
    const headers = [
      "Vendor Name", "Department"
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vendors");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `vendors_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setUploadForm({ ...uploadForm, file });

    if (errors.file) {
      setErrors({ ...errors, file: undefined });
    }

    setImportStatus('idle');
    setImportMessage('');
    setImportResult(null);
    setFailedVendors([]);
  };

  const parseFieldError = (errorMessage: string): string[] => {
    const fieldErrors: string[] = [];

    const errors = errorMessage.split(';').map(err => err.trim());

    errors.forEach(error => {
      // Common field error patterns
      if (error.toLowerCase().includes('vendor name') || error.toLowerCase().includes('name is required')) {
        fieldErrors.push('name');
      }
      if (error.toLowerCase().includes('department')) {
        fieldErrors.push('department');
      }
    });

    if (errorMessage.toLowerCase().includes('unknown column') || errorMessage.toLowerCase().includes('field list')) {
      fieldErrors.push('name', 'department');
    }

    if (fieldErrors.length === 0) {
      fieldErrors.push('name', 'department');
    }

    return fieldErrors;
  };

  const getFieldSpecificError = (errorMessage: string, fieldName: string): string => {
    const errors = errorMessage.split(';').map(err => err.trim());

    for (const error of errors) {
      const lowerError = error.toLowerCase();
      const lowerField = fieldName.toLowerCase();

      // Check for field-specific error patterns
      if (lowerField === 'name' && (lowerError.includes('vendor name') || lowerError.includes('name is required'))) {
        return error;
      }
      if (lowerField === 'department' && lowerError.includes('department')) {
        return error;
      }
    }

    return errors[0] || errorMessage;
  };

  const createFailedVendorData = (error: ImportError): VendorData => {
    const vendorData = error.vendorData || {
      name: error.vendor === 'Unknown' ? '' : error.vendor,
      department: '',
    };

    return {
      name: vendorData.name || '',
      department: vendorData.department || '',
    };
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};

    if (!uploadForm.file) {
      newErrors.file = 'Please select a file to upload';
    } else {
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (uploadForm.file.size > maxSize) {
        newErrors.file = 'File size must be less than 10MB';
      }

      const allowedTypes = ['.xlsx', '.xls'];
      const fileExtension = '.' + uploadForm.file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        newErrors.file = 'Please select a valid Excel file';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
 
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!validateForm() || !uploadForm.file) {
      return;
    }

    try {
      setLoading(true);
      setImportStatus('idle');
      setImportMessage('Processing file...');

      // Call the bulk import API
      const result = await vendorsAPI.bulkImport(uploadForm.file);

      if (result.success) {
        setImportStatus('success');
        setImportMessage(result.message);

        // Process the result data
        if (result.data) {
          setImportResult(result.data);

          if (result.data.errors && result.data.errors.length > 0) {
            const failedVendorData = result.data.errors.map(createFailedVendorData);
            setFailedVendors(failedVendorData);
          }
        }

        setUploadForm({ file: null });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setImportStatus('error');
        setImportMessage(result.message || 'Import failed');
      }
    } catch (error: any) {
      setImportStatus('error');
      setImportMessage(error.response?.data?.error || 'Import failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Vendors</h1>
          <p className="mt-1 text-sm text-gray-500">
            Import vendor data from Excel files.
          </p>
        </div>

        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={handleExportExcel}
            className="btn-secondary flex items-center">
            Sample Excel
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="card">
        <div className="card-body">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Building2 className="mx-auto h-16 w-16 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">Import Vendors</h3>
              <p className="mt-2 text-sm text-gray-500">
                Upload an Excel file to import vendor data into the system.
              </p>
            </div>

            {/* File Upload Section */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Vendor File *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className={`input-field ${errors.file ? 'border-red-500' : ''}`}
                  onChange={handleFileChange}
                />
                <p className="text-xs text-gray-500 mt-1">Supported formats: Excel (.xlsx, .xls) (Max 10MB)</p>
                {errors.file && <p className="text-red-500 text-xs mt-1">{errors.file}</p>}
              </div>

              {/* Submit Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleSubmit}
                  disabled={!uploadForm.file || loading}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="w-4 h-4" />
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Import Vendors
                </button>
              </div>

              {/* Status Message */}
              {importStatus !== 'idle' && (
                <div className="text-center">
                  {importStatus === 'success' && (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">{importMessage}</span>
                    </div>
                  )}
                  {importStatus === 'error' && (
                    <div className="flex items-center justify-center gap-2 text-red-600">
                      <XCircle className="w-4 h-4" />
                      <span className="text-sm">{importMessage}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Results Table - Show only when there are errors */}
      {importResult && importResult.errors && importResult.errors.length > 0 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-red-600">Import Errors</h3>
              <div className="text-sm text-gray-500">
                {importResult.failed} of {importResult.total} vendors failed to import
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-red-600 uppercase tracking-wider">Row</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-red-600 uppercase tracking-wider">Vendor Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-red-600 uppercase tracking-wider">Department</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-red-600 uppercase tracking-wider">Error Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {importResult.errors.map((error, index) => {
                    const fieldErrors = parseFieldError(error.error);
                    const vendorData = failedVendors[index] || createFailedVendorData(error);

                    return (
                      <tr key={index} className="bg-red-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {index + 1}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${fieldErrors.includes('name') ? 'bg-red-200 text-red-800' : 'text-gray-900'}`}>
                          <div className={`${fieldErrors.includes('name') ? 'relative group cursor-help' : ''}`}>
                            {vendorData.name || '-'}
                            {fieldErrors.includes('name') && (
                              <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 max-w-xs">
                                <div className="whitespace-normal">
                                  {getFieldSpecificError(error.error, 'name')}
                                </div>
                                <div className="absolute top-full left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                              </div>
                            )}
                          </div>
                        </td>
                        
                        
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${fieldErrors.includes('department') ? 'bg-red-200 text-red-800' : 'text-gray-900'}`}>
                          <div className={`${fieldErrors.includes('department') ? 'relative group cursor-help' : ''}`}>
                            {vendorData.department || '-'}
                            {fieldErrors.includes('department') && (
                              <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 max-w-xs">
                                <div className="whitespace-normal">
                                  {getFieldSpecificError(error.error, 'department')}
                                </div>
                                <div className="absolute top-full left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-red-600 font-medium">
                          <div className="space-y-1">
                            {error.error.split(';').map((err, idx) => (
                              <div key={idx} className="flex items-start">
                                <span className="text-red-500 mr-2">•</span>
                                <span className="">{err.trim()}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportVendor;
